import {Group, type Object3D, type Vector3} from 'three'

import {yawToFace} from './hit-test'

/**
 * Owns the placed object's world transform.
 *
 * Transform ownership is split deliberately:
 *
 *   root    <- this controller: position, placement yaw, user pinch scale
 *    └ pivot <- the model normaliser: unit scale, y-offset, authored yaw
 *       └ content = gltf.scene: centering, up-axis fix
 *
 * With two owners and no overlap, a placement bug can never corrupt the
 * normalisation and vice versa.
 *
 * OWNERSHIP: this file is the only thing allowed to write the placed content's
 * `visible`. `place()` always hides it and `reveal()` always shows it, with no
 * knowledge here of whether a lead-in is configured - the caller decides how
 * long the gap is, and with no lead-in the two happen in the same JS turn, so
 * nothing is ever drawn in between and placement stays bit-for-bit as it was.
 *
 * Deliberately the CONTENT and not `root`: the shadow catcher, the entrance
 * effect and the lead-in trail all hang off `root` too, and they have to be
 * visible during the gap. Hiding `root` would take the trail with it.
 */
export interface PlacementController {
  readonly root: Group
  readonly isPlaced: boolean
  /**
   * Attaches (or replaces) what gets placed. Separate from construction because
   * the model streams in behind the already-running camera feed.
   */
  setContent(content: Object3D): void
  /** Positions and aims the root, and hides the content until `reveal()`. */
  place(position: Vector3, cameraPosition: Vector3): void
  /**
   * Shows the content. The moment the character arrives.
   *
   * Called from exactly one place - `revealPlaced()` in engine/app-module.ts -
   * because it has to happen on the same frame as `playIntro()`.
   */
  reveal(): void
  clear(): void
}

export const createPlacementController = (): PlacementController => {
  const root = new Group()
  root.name = 'placement-root'
  root.visible = false

  let placed = false
  // Held rather than discarded so `reveal()` has something to show. `root`'s
  // children are not a substitute: the effects live there too.
  let content: Object3D | null = null

  return {
    root,
    setContent: (next) => {
      root.clear()
      root.add(next)
      content = next
    },
    get isPlaced() {
      return placed
    },
    place: (position, cameraPosition) => {
      root.position.copy(position)
      root.rotation.set(0, yawToFace(position, cameraPosition), 0)
      // The root goes visible here regardless: the shadow catcher, the entrance
      // effect and the lead-in trail hang off it and are what the user sees
      // during the gap.
      root.visible = true
      // Unconditional, and paired with an unconditional `reveal()`. Making this
      // depend on the lead-in would put a second copy of "is there a lead-in"
      // here, where the answer would have to agree with the sequencer's forever.
      if (content !== null) content.visible = false
      placed = true
    },
    reveal: () => {
      if (content !== null) content.visible = true
    },
    clear: () => {
      root.visible = false
      // Hand the content back visible. `place()` hides it on the way in, so
      // leaving it hidden here would make the next `setContent` -> `place`
      // cycle start from a state this file never intends to be in.
      if (content !== null) content.visible = true
      placed = false
    },
  }
}
